import { useCallback, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { ErrorState } from '../../components/ui/States';
import { SegmentedControl, type TabItem } from '../../components/ui/Tabs';
import { Input } from '../../components/ui/Input';
import { formatDayMonthYear } from '../../utils/dateFormat';

/**
 * Small pieces shared by the Marketing tabs, so every tab has the same period
 * control, the same "some sources failed" note and the same toolbar row.
 */

export type DayRange = '7d' | '30d' | '90d' | '180d';

export const DAY_RANGES: Record<DayRange, number> = { '7d': 7, '30d': 30, '90d': 90, '180d': 180 };

export function rangeOptions<T extends DayRange>(ids: readonly T[]): TabItem<T>[] {
  return ids.map((id) => ({ id, label: `${DAY_RANGES[id]} days` }));
}

/**
 * The one period control for every Marketing tab: the same chips and the same
 * default everywhere, and the choice rides along in `?range=` so it carries
 * across tabs (the tab bar keeps the param when it navigates).
 */
export const MARKETING_RANGE_IDS: readonly DayRange[] = ['7d', '30d', '90d', '180d'];
export const DEFAULT_MARKETING_RANGE: DayRange = '90d';
export const MARKETING_RANGE_OPTIONS = rangeOptions(MARKETING_RANGE_IDS);
export const RANGE_PARAM = 'range';

export function isDayRange(value: string | null | undefined): value is DayRange {
  return !!value && (MARKETING_RANGE_IDS as readonly string[]).includes(value);
}

/** "Last 90 days" — the period wording every tile sub-line uses. */
export function rangeLabel(range: DayRange): string {
  return `Last ${DAY_RANGES[range]} days`;
}

/** The shared Marketing period, read from and written to `?range=`. */
export function useMarketingRange(): [DayRange, (next: DayRange) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(RANGE_PARAM);
  const range = isDayRange(raw) ? raw : DEFAULT_MARKETING_RANGE;
  const setRange = useCallback(
    (next: DayRange) =>
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(RANGE_PARAM, next);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  return [range, setRange];
}

/** The shared period chips, wired to {@link useMarketingRange}. */
export function MarketingRangeControl() {
  const [range, setRange] = useMarketingRange();
  return <SegmentedControl ariaLabel="Period" options={MARKETING_RANGE_OPTIONS} value={range} onChange={setRange} />;
}

export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The control row above a tab's content: context on the left, period on the right. */
export function Toolbar({ start, end }: { start?: ReactNode; end?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">{start}</div>
      <div className="flex flex-wrap items-center gap-2">{end}</div>
    </div>
  );
}

/**
 * Sources the API could not reach for this response. A real failure, so it
 * stays visible — as one quiet line naming each source and, when the API sent
 * one, why it failed (`reasons`, keyed by the same source key).
 */
export function UnavailableSources({
  sources,
  label = (s: string) => s,
  reasons,
}: {
  sources: string[];
  label?: (s: string) => string;
  reasons?: Record<string, string> | null;
}) {
  if (sources.length === 0) return null;
  const keys = [...new Set(sources)];
  const names = [...new Set(keys.map(label))];
  const lines = keys.map((k) => `${label(k)}: ${reasons?.[k] ?? 'no reason reported (see the API logs)'}`);
  const subject = names.length <= 3 ? names.join(', ') : `${names.length} sources`;
  return (
    <ErrorState
      compact
      title={`${subject} didn’t load, so ${names.length === 1 ? 'its panels are' : 'their panels are'} empty`}
      error={lines.join(' · ')}
      details={
        keys.length > 1 && (
        <ul className="space-y-0.5">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
        )
      }
    />
  );
}

/** "Paid_google" / "paid_google" / "google-ads" → "Paid search (Google)"; other slugs → sentence case. */
const CHANNEL_LABELS: Record<string, string> = {
  paid_google: 'Paid search (Google)',
  paid_bing: 'Paid search (Bing)',
  paid_linkedin: 'Paid social (LinkedIn)',
  paid_meta: 'Paid social (Meta)',
  google_ads: 'Paid search (Google)',
  bing_ads: 'Paid search (Bing)',
  linkedin_ads: 'Paid social (LinkedIn)',
  meta_ads: 'Paid social (Meta)',
  paid: 'Paid',
  paid_search: 'Paid search',
  paid_social: 'Paid social',
  organic: 'Organic',
  organic_search: 'Organic search',
  organic_social: 'Organic social',
  direct: 'Direct',
  '(direct)': 'Direct',
  referral: 'Referral',
  email: 'Email',
  email_outbound: 'Outbound email',
  linkedin_outbound: 'Outbound LinkedIn',
  outbound: 'Outbound',
  social: 'Organic social',
  ai: 'AI assistants',
  ai_assistant: 'AI assistants',
  llm: 'AI assistants',
  unassigned: 'Unattributed',
  '(not set)': 'Unattributed',
  qr: 'QR code',
  internal: 'Internal / debug',
  other: 'Other',
};

/**
 * `channel` is either already a website channel (the API classifies the
 * channel table) or a stored LeadChannel, where "organic" lumps social, email,
 * AI assistants, QR codes and referrals together. Pass the source (UTM source
 * or referrer host) and medium and an "organic" row is split out for display,
 * so older rows read right too.
 */
export function channelLabel(channel: string, source?: string | null, medium?: string | null): string {
  const key = channel.trim().toLowerCase().replace(/-/g, '_');
  // A paid click from a platform without its own channel ("openai / cpc") names the
  // platform, rather than sitting alone under a bare "Paid" heading.
  if (key === 'paid' && source && !BLANK_RE.test(source.trim().toLowerCase())) return `Paid (${paidPlatformName(source)})`;
  if (key === 'organic' && (source || medium)) {
    // A stored "organic" with no UTMs came in from a referrer we no longer have here.
    const split = trafficChannel(source, medium);
    return CHANNEL_LABELS[split === 'direct' ? 'referral' : split] ?? 'Other';
  }
  return CHANNEL_LABELS[key] ?? humanize(channel);
}

const BLANK_RE = /^(|\(none\)|\(not set\)|\(direct\))$/;

const PAID_PLATFORM_NAMES: Record<string, string> = { openai: 'OpenAI', chatgpt: 'OpenAI', 'chatgpt.com': 'OpenAI', bing: 'Bing', microsoft: 'Microsoft' };

/** "openai" → "OpenAI", other sources sentence-cased: the platform behind a paid click. */
export function paidPlatformName(source: string): string {
  const s = source.trim().toLowerCase();
  return PAID_PLATFORM_NAMES[s] ?? humanize(s);
}
const AI_RE = /chatgpt|openai|perplexity|claude\.ai|gemini|copilot|you\.com|mistral|deepseek/;
const SEARCH_RE = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|qwant|startpage|yandex|baidu|search\.brave)(\.|$)/;
const NOT_SEARCH_RE = /^(mail|docs|drive|calendar|meet|accounts|analytics|ads|sites|groups|support|cloud|keep|photos|translate|chat|classroom|forms)\./;
const INTERNAL_RE = /(^|\.)tagassistant\.google\.com$/;
const SOCIAL_RE = /linkedin|lnkd\.in|instagram|facebook|fb\.com|^ig$|^fb$|^x$|^x\.com|twitter|t\.co$|youtube|tiktok|reddit/;
const META_SOURCE_RE = /facebook|instagram|meta|^ig$|^fb$|^msg$|^an$/;
/** Meta's {{placement}} macro (Instagram_Feed, Facebook_Mobile_Feed…) only exists in ad URLs: a paid click. */
const META_PLACEMENT_RE = /^(facebook|instagram|messenger|threads|audience_network|an)_[a-z_]+$/;

/**
 * Web mirror of the API's `classifyWebChannel`
 * (packages/api/src/modules/analytics/web-channel.ts); keep them in sync.
 * `source` is the UTM source, or the referrer host when there were no UTMs.
 */
export function trafficChannel(source?: string | null, medium?: string | null): string {
  const s = (source ?? '').trim().toLowerCase();
  const m = (medium ?? '').trim().toLowerCase();
  if (INTERNAL_RE.test(s)) return 'internal';
  if (BLANK_RE.test(s) && BLANK_RE.test(m)) return 'direct';
  // `li_fat_id` / `gclid` / `msclkid`: the API's label for an untagged ad click (web-analytics.ts).
  if (/paid|cpc|ppc|cpm|cpv|display|retargeting|banner|^li_fat_id$|^gclid$|^msclkid$/.test(m)) {
    if (/linkedin|lnkd/.test(s)) return 'paid_linkedin';
    if (META_SOURCE_RE.test(s)) return 'paid_meta';
    if (/google|youtube|doubleclick/.test(s)) return 'paid_google';
    if (/bing|microsoft|msn/.test(s)) return 'paid_bing';
    return 'paid';
  }
  if (META_SOURCE_RE.test(s) && META_PLACEMENT_RE.test(m)) return 'paid_meta';
  if (/^qr/.test(m) || /^qr/.test(s)) return 'qr';
  if (/outbound|sdr|cold_email|prospecting/.test(m + ' ' + s) || ['outreach', 'attio', 'salesloft', 'apollo', 'lemlist'].includes(s)) return 'outbound';
  if (/e-?mail|newsletter|customer\.?io/.test(m + ' ' + s)) return 'email';
  if (AI_RE.test(s)) return 'ai';
  if (SOCIAL_RE.test(s) || /social/.test(m)) return 'social';
  if (SEARCH_RE.test(s) && !NOT_SEARCH_RE.test(s) && (BLANK_RE.test(m) || m === 'organic' || m === 'referral')) return 'organic_search';
  if (BLANK_RE.test(m) || m === 'referral' || m === 'affiliate') return 'referral';
  return 'other';
}

/**
 * Traffic or form rows tagged during QA (utm_source=test, "testing"…). They're not
 * marketing data, so tables hide them and say how many were hidden.
 */
export function isTestSource(...values: (string | null | undefined)[]): boolean {
  return values.some((v) => !!v && /test/i.test(v));
}

export { isTestEmail, isTestPerson } from './known-people';

/** `seo_content` → "SEO content", `buyerName` → "Buyer name". */
export function humanize(slug: string): string {
  const spaced = slug
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  const cased = spaced.replace(/\b(seo|icp|ai|saas|linkedin|utm|url|gsc|ga4|b2b|rfp)\b/g, (w) => WORD_CASE[w] ?? w);
  return cased.charAt(0).toUpperCase() + cased.slice(1);
}

const WORD_CASE: Record<string, string> = {
  seo: 'SEO', icp: 'ICP', ai: 'AI', saas: 'SaaS', linkedin: 'LinkedIn', utm: 'UTM',
  url: 'URL', gsc: 'GSC', ga4: 'GA4', b2b: 'B2B', rfp: 'RFP',
};

/**
 * A panel group that renders its content only once opened — for heavy,
 * rarely-read sections (each one fans out to live sources).
 */
export function CollapsibleSection({
  title,
  description,
  children,
  defaultOpen = false,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  // A plain disclosure row, not a card: its panels are cards, and cards inside a card double every border.
  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 border-b border-border-light py-3 text-left"
      >
        <span className="min-w-0">
          <span className="text-body-md font-medium text-text-primary">{title}</span>
          {description && <span className="ml-2 text-body-sm text-text-tertiary">{description}</span>}
        </span>
        <ChevronDown
          className={`h-4 w-4 flex-none text-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && <div className="space-y-6 pt-6">{children}</div>}
    </section>
  );
}

/** A tab's period when its endpoints take dates: the shared chips plus Custom. */
export type MarketingPeriodRange = DayRange | 'custom';

const PERIOD_OPTIONS: readonly TabItem<MarketingPeriodRange>[] = [...MARKETING_RANGE_OPTIONS, { id: 'custom', label: 'Custom' }];

/**
 * The period in `?range=` (plus `?cstart=`/`?cend=` for Custom), as dates and a
 * label for tile sub-lines. Used by Website and Opportunities.
 */
export function useMarketingPeriod() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(RANGE_PARAM);
  const range: MarketingPeriodRange = raw === 'custom' || isDayRange(raw) ? raw : DEFAULT_MARKETING_RANGE;
  const customStart = searchParams.get('cstart') || isoDaysAgo(30);
  const customEnd = searchParams.get('cend') || isoToday();
  const period =
    range === 'custom'
      ? { startDate: customStart, endDate: customEnd }
      : { startDate: isoDaysAgo(DAY_RANGES[range]), endDate: isoToday() };
  const label =
    range === 'custom' ? `${formatDayMonthYear(customStart)} to ${formatDayMonthYear(customEnd)}` : rangeLabel(range);
  const setParam = (key: string, value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );
  return { range, period, label, customStart, customEnd, setParam };
}

/** Period chips with Custom; the custom dates show only when Custom is picked. */
export function MarketingPeriodControl({ p }: { p: ReturnType<typeof useMarketingPeriod> }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {p.range === 'custom' && (
        <div className="flex items-center gap-1.5">
          <Input
            type="date"
            aria-label="Start date"
            value={p.customStart}
            max={p.customEnd}
            onChange={(e) => p.setParam('cstart', e.target.value)}
            className="!py-1 text-body-xs"
          />
          <span className="text-body-xs text-text-tertiary">to</span>
          <Input
            type="date"
            aria-label="End date"
            value={p.customEnd}
            min={p.customStart}
            max={isoToday()}
            onChange={(e) => p.setParam('cend', e.target.value)}
            className="!py-1 text-body-xs"
          />
        </div>
      )}
      <SegmentedControl ariaLabel="Period" options={PERIOD_OPTIONS} value={p.range} onChange={(id) => p.setParam(RANGE_PARAM, id)} />
    </div>
  );
}
